import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Radio, Bookmark, User, LayoutGrid, LogOut, Newspaper, CalendarDays, CreditCard, LifeBuoy, Rss, X, MessageCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ALL_LINKS = [
  { to: "/app", label: "Feed", icon: Newspaper, end: true, tid: "nav-feed" },
  { to: "/app/categories", label: "Niches", icon: LayoutGrid, tid: "nav-categories" },
  { to: "/app/feeds", label: "My feeds", icon: Rss, tid: "nav-feeds" },
  { to: "/app/calendar", label: "Calendar", icon: CalendarDays, tid: "nav-calendar" },
  { to: "/app/saved", label: "Saved", icon: Bookmark, tid: "nav-saved" },
  { to: "/app/billing", label: "Billing", icon: CreditCard, tid: "nav-billing", requires: "billing" },
  { to: "/app/profile", label: "Profile", icon: User, tid: "nav-profile" },
];

export default function Sidebar({ mobileOpen = false, onClose = () => {} }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const billingEnabled = !!user?.billing_enabled;
  const links = ALL_LINKS.filter((l) => l.requires !== "billing" || billingEnabled);

  const onLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <>
      {/* Mobile scrim */}
      {mobileOpen && (
        <div
          data-testid="sidebar-scrim"
          onClick={onClose}
          className="fixed inset-0 z-40 bg-black/40 lg:hidden animate-[fadeIn_180ms_ease-out_both]"
        />
      )}

      <aside
        data-testid="sidebar"
        className={
          // Desktop: sticky sidebar. Mobile: off-canvas drawer sliding in from left.
          `bg-gray-50 border-r border-gray-200 flex flex-col z-50 ` +
          `fixed lg:sticky top-0 h-screen w-72 lg:w-64 shrink-0 ` +
          `transition-transform duration-200 ease-out ` +
          (mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0")
        }
      >
        <div className="px-6 py-5 lg:py-6 border-b border-gray-200 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="brand-mark"><span /><span /><span /></span>
              <span className="font-display text-[22px] tracking-[-0.02em] font-semibold leading-none">Fount</span>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-gradient pulse-dot" />
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500">
                Live · 72h window
              </span>
            </div>
          </div>
          <button
            data-testid="sidebar-close"
            onClick={onClose}
            aria-label="Close menu"
            className="lg:hidden -mr-2 -mt-1 p-2 text-gray-500 hover:text-black"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          {links.map(({ to, label, icon: Icon, end, tid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={tid}
              className={({ isActive }) =>
                `nav-link relative flex items-center gap-3 px-6 py-3.5 lg:py-3 text-[15px] lg:text-sm transition-colors ${
                  isActive
                    ? "bg-white text-[#0a0a0a] font-semibold is-active"
                    : "text-gray-600 hover:bg-gray-100 hover:text-[#0a0a0a]"
                }`
              }
            >
              <Icon size={16} strokeWidth={1.5} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-gray-200 px-6 py-4">
          <PlanBadge user={user} />
          <div className="text-xs font-mono uppercase tracking-widest text-gray-500 mb-1 mt-4">
            Signed in
          </div>
          <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">
            {user?.name || user?.email}
          </div>
          <div className="mt-3 flex items-center gap-5 flex-wrap">
            <button
              data-testid="logout-button"
              onClick={onLogout}
              className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-gray-500 hover:text-[#0a0a0a]"
            >
              <LogOut size={14} strokeWidth={1.5} />
              Sign out
            </button>
            <NavLink
              to="/feedback"
              data-testid="sidebar-feedback"
              className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-gray-500 hover:text-[#0a0a0a]"
            >
              <MessageCircle size={14} strokeWidth={1.5} />
              Feedback
            </NavLink>
            <NavLink
              to="/support"
              data-testid="sidebar-support"
              className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-gray-500 hover:text-[#0a0a0a]"
            >
              <LifeBuoy size={14} strokeWidth={1.5} />
              Support
            </NavLink>
          </div>
        </div>
      </aside>
    </>
  );
}

function PlanBadge({ user }) {
  const billingEnabled = !!user?.billing_enabled;

  if (!billingEnabled) {
    return (
      <div data-testid="plan-badge" className="bg-white border border-gray-200 p-3 rounded-lg">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500">Access</span>
          <span
            className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-brand-gradient text-white"
            data-testid="plan-badge-label"
          >
            Early access
          </span>
        </div>
        <div className="text-[11px] font-mono text-gray-600 mt-2">All features unlocked</div>
      </div>
    );
  }

  const plan = (user?.plan || "free").toLowerCase();
  const feats = user?.plan_features || {};
  const usage = user?.usage || { generations: 0, lifetime_generations: 0 };
  const metering = feats.metering || "monthly";
  const limit = feats.generations_limit ?? feats.generations_per_month;
  const planLabel = feats.name || "Free";
  const showUpgrade = plan !== "studio";
  const used = metering === "lifetime"
    ? (usage.lifetime_generations || 0)
    : (usage.generations || 0);

  let usageLine;
  if (limit === -1 || limit === undefined) {
    usageLine = `${used} generations this month`;
  } else if (metering === "lifetime") {
    usageLine = `${used} / ${limit} generations · lifetime`;
  } else {
    usageLine = `${used} / ${limit} generations`;
  }
  const pct = limit && limit !== -1 ? Math.min(100, Math.round((used / limit) * 100)) : 0;

  return (
    <div data-testid="plan-badge" className="bg-white border border-gray-200 p-3 rounded-lg">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500">Plan</span>
        <span
          className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${
            plan === "free"
              ? "border border-gray-300 text-gray-700"
              : "bg-brand-gradient text-white"
          }`}
          data-testid="plan-badge-label"
        >
          {planLabel}
        </span>
      </div>
      <div className="text-[11px] font-mono text-gray-600 mt-2">{usageLine}</div>
      {limit && limit !== -1 && (
        <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-1 bg-brand-gradient transition-all"
            style={{ width: `${pct}%` }}
            data-testid="plan-usage-bar"
          />
        </div>
      )}
      {showUpgrade && (
        <NavLink
          to="/pricing"
          data-testid="sidebar-upgrade"
          className="mt-3 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-gradient font-semibold hover:underline"
        >
          Upgrade →
        </NavLink>
      )}
    </div>
  );
}
